/**
 * Create or edit an SOP (plan docs/plans/lms-plan.md 11.1 "Create SOP
 * (admin): number, title, category, owner"; Appendix A POST /admin/sops,
 * PATCH /admin/sops/:id; 18.25; WP-46).
 *
 * The SOP number is fixed once created (it names the training course,
 * sop-{number}, 11.2), so the edit form shows it read-only. A number the
 * network already has (in any case) is 409 { existing_sop }: the form says so
 * and offers to open that SOP. The owner is one person the admin reaches,
 * chosen with WP-43's UserPicker (the directory, G14); the picker allows
 * several, so the last one ticked is the owner.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage } from '../../api/client';
import { createSop, SOP_CATEGORY_MAX, SOP_NUMBER_MAX, SOP_TITLE_MAX, updateSop } from '../../api/sops';
import type { SopRow } from '../../api/sops';
import { ErrorNote, labelCls, linkBtnCls, metaCls } from '../../components/pickers/shared';
import UserPicker from '../../components/pickers/UserPicker';

export interface SopFormModalProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  onSaved: (sop: SopRow) => void;
  /** Edit mode: the SOP as it stands. */
  sop?: { id: string; sop_number: string; title: string; category: string | null; owner_user_id: string | null; owner?: { id: string; full_name: string | null } | null } | null;
  /** Create mode: open the SOP that already carries the number (409 existing_sop). */
  onOpenExisting?: (sopId: string) => void;
}

interface Existing {
  id: string;
  status: string;
}

function existingOf(err: unknown): Existing | null {
  const data = (err as { response?: { data?: { existing_sop?: Existing } } } | null)?.response?.data;
  const e = data?.existing_sop;
  return e && typeof e.id === 'string' ? e : null;
}

export function SopFormModal({ mode, isOpen, onClose, onSaved, sop = null, onOpenExisting }: SopFormModalProps) {
  const [number, setNumber] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [owner, setOwner] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<Existing | null>(null);

  // A fresh form every time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    setNumber(sop?.sop_number ?? '');
    setTitle(sop?.title ?? '');
    setCategory(sop?.category ?? '');
    setOwner(sop?.owner_user_id ? [sop.owner_user_id] : []);
    setBusy(false);
    setError(null);
    setExisting(null);
  }, [isOpen, sop]);

  const ownerId = owner.length > 0 ? owner[owner.length - 1] : null;
  const missing = (mode === 'create' && !number.trim()) || !title.trim();

  const save = async () => {
    if (busy || missing) return;
    setBusy(true);
    setError(null);
    setExisting(null);
    try {
      if (mode === 'create') {
        const created = await createSop({ sop_number: number, title, category, owner_user_id: ownerId });
        onSaved(created);
      } else if (sop) {
        const patch: { title?: string; category?: string | null; owner_user_id?: string | null } = {};
        if (title.trim() !== sop.title) patch.title = title.trim();
        if ((category.trim() || null) !== (sop.category ?? null)) patch.category = category.trim() || null;
        if ((ownerId ?? null) !== (sop.owner_user_id ?? null)) patch.owner_user_id = ownerId;
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        const saved = await updateSop(sop.id, patch);
        onSaved(saved);
      }
    } catch (err) {
      setExisting(mode === 'create' ? existingOf(err) : null);
      setError(apiErrorMessage(err, mode === 'create' ? 'The SOP could not be created.' : 'The SOP could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={mode === 'create' ? 'New SOP' : `Edit SOP ${sop?.sop_number ?? ''}`} maxWidth="max-w-2xl">
      <form
        className="space-y-4"
        data-testid="lms-sop-form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-[minmax(0,10rem)_1fr]">
          <div>
            <label htmlFor="lms-sop-number" className={labelCls}>
              SOP number
            </label>
            {mode === 'create' ? (
              <input
                id="lms-sop-number"
                data-testid="lms-sop-number"
                className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
                value={number}
                maxLength={SOP_NUMBER_MAX}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="4.2"
                autoComplete="off"
              />
            ) : (
              <p className="py-1.5 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">{sop?.sop_number}</p>
            )}
          </div>
          <div>
            <label htmlFor="lms-sop-title" className={labelCls}>
              Title
            </label>
            <input
              id="lms-sop-title"
              data-testid="lms-sop-title"
              className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
              value={title}
              maxLength={SOP_TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Informed consent process"
            />
          </div>
        </div>
        {mode === 'create' && <p className={metaCls}>The number cannot change later: it names the SOP training course.</p>}
        <div>
          <label htmlFor="lms-sop-category" className={labelCls}>
            Category <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
          </label>
          <input
            id="lms-sop-category"
            data-testid="lms-sop-category"
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0 sm:max-w-sm`}
            value={category}
            maxLength={SOP_CATEGORY_MAX}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Regulatory"
          />
        </div>
        <div>
          <UserPicker
            value={owner}
            onChange={(ids) => setOwner(ids.length > 0 ? [ids[ids.length - 1]] : [])}
            label="Owner (optional)"
            pageSize={25}
            disabled={busy}
          />
          {mode === 'edit' && sop?.owner && ownerId === sop.owner_user_id && (
            <p className={`${metaCls} mt-1`}>{`Current owner: ${sop.owner.full_name || 'Unnamed person'}`}</p>
          )}
        </div>
        {error && (
          <ErrorNote testId="lms-sop-form-error">
            {error}
            {existing && onOpenExisting && (
              <>
                {' '}
                <button type="button" className={linkBtnCls} onClick={() => onOpenExisting(existing.id)} data-testid="lms-sop-open-existing">
                  {existing.status === 'retired' ? 'Open the retired SOP' : 'Open that SOP'}
                </button>
              </>
            )}
          </ErrorNote>
        )}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={busy || missing} data-testid="lms-sop-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {mode === 'create' ? 'Create SOP' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default SopFormModal;
